import { Drawer as BaseDrawer } from '@base-ui/react/drawer'
import { type VariantProps } from 'class-variance-authority'
import { use } from 'react'

import { PortalContainerContext } from '../../../lib/portal-container-context.ts'
import { cn } from '../../../lib/utils.ts'

import { popupVariants } from './drawer.variants.ts'

/**
 * Manages the drawer state, swipe direction and snap points.
 *
 * @summary Drawer state root.
 */
function DrawerRoot(props: DrawerRoot.Props) {
  return <BaseDrawer.Root {...props} />
}
namespace DrawerRoot {
  export type Props = BaseDrawer.Root.Props
}

function DrawerTrigger(props: DrawerTrigger.Props) {
  return <BaseDrawer.Trigger data-slot="drawer-trigger" {...props} />
}
namespace DrawerTrigger {
  export type Props = BaseDrawer.Trigger.Props
}

function DrawerPortal(props: DrawerPortal.Props) {
  const { container, ...rest } = props
  const scopeContainer = use(PortalContainerContext)
  // Base UI renders nothing for `container={null}`, so an absent scope host must become `undefined`
  // to get its document.body fallback.
  return <BaseDrawer.Portal data-slot="drawer-portal" container={container ?? scopeContainer ?? undefined} {...rest} />
}
namespace DrawerPortal {
  export type Props = BaseDrawer.Portal.Props
}

function DrawerClose(props: DrawerClose.Props) {
  return <BaseDrawer.Close data-slot="drawer-close" {...props} />
}
namespace DrawerClose {
  export type Props = BaseDrawer.Close.Props
}

function DrawerViewport(props: DrawerViewport.Props) {
  const { className, ...rest } = props
  return (
    <BaseDrawer.Viewport
      data-slot="drawer-viewport"
      className={cn('pointer-events-none fixed inset-0 z-50 select-none', className)}
      {...rest}
    />
  )
}
namespace DrawerViewport {
  export type Props = BaseDrawer.Viewport.Props
}

// Enter is a keyframe, not a `data-starting-style` transition, for the same reason as `Popup` (see
// `drawer.variants.ts`): Base UI only emits `data-starting-style` when `open` flips false -> true,
// so a drawer mounted already open (route-driven panel) would get no fade at all and the backdrop
// would snap to full opacity while the panel slides. Exit stays a transition so a swipe release
// interpolates from wherever the finger let go.
function DrawerBackdrop(props: DrawerBackdrop.Props) {
  const { className, ...rest } = props
  return (
    <BaseDrawer.Backdrop
      data-slot="drawer-backdrop"
      className={cn(
        `
          fixed inset-0 z-40 min-h-dvh bg-black/32
          opacity-[max(var(--drawer-overlay-min-opacity,0),calc(1-var(--drawer-swipe-progress)))] transition-opacity
          motion-enter-emphasized select-none
          data-ending-style:pointer-events-none data-ending-style:opacity-0 data-ending-style:motion-exit-emphasized
          data-open:animate-in data-open:fade-in-0
          data-swiping:duration-0
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace DrawerBackdrop {
  export type Props = BaseDrawer.Backdrop.Props
}

/**
 * The styled drawer surface. Its `size` sets the width of horizontal drawers; vertical drawers size to their content.
 *
 * @summary Drawer content surface.
 */
function DrawerPopup(props: DrawerPopup.Props) {
  const { className, size = 'medium', ...rest } = props
  return <BaseDrawer.Popup data-slot="drawer-popup" className={cn(popupVariants({ size, className }))} {...rest} />
}
namespace DrawerPopup {
  type Variants = NonNullable<VariantProps<typeof popupVariants>>
  export type Props = BaseDrawer.Popup.Props & {
    /** Panel width on desktop; horizontal directions only. Full width below `md`. @default 'medium' */
    size?: Variants['size']
  }
}

function DrawerContent(props: DrawerContent.Props) {
  const { className, ...rest } = props
  return (
    <BaseDrawer.Content
      data-slot="drawer-content"
      className={cn(
        `
          flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain rounded-[inherit] transition-opacity
          motion-move select-text
          group-data-nested-drawer-open/drawer-popup:opacity-0
          group-data-nested-drawer-swiping/drawer-popup:opacity-100
          group-data-swiping/drawer-popup:select-none
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace DrawerContent {
  export type Props = BaseDrawer.Content.Props
}

// Body1SemiBold is a Figma decision for the side-panel chrome title; do not harmonise it with
// `Dialog.Title`.
function DrawerTitle(props: DrawerTitle.Props) {
  const { className, ...rest } = props
  return (
    <BaseDrawer.Title
      data-slot="drawer-title"
      className={cn('m-0 font-body text-30 leading-40 font-600 tracking-20 text-content-base-main', className)}
      {...rest}
    />
  )
}
namespace DrawerTitle {
  export type Props = BaseDrawer.Title.Props
}

function DrawerDescription(props: DrawerDescription.Props) {
  const { className, ...rest } = props
  return (
    <BaseDrawer.Description
      data-slot="drawer-description"
      className={cn('font-body text-20 leading-30 font-400 tracking-20 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace DrawerDescription {
  export type Props = BaseDrawer.Description.Props
}

/**
 * `Drawer` is a multi-part primitive: `Drawer.Root` + `Drawer.Trigger` + `Drawer.Portal`
 * + `Drawer.Backdrop` + `Drawer.Viewport` + `Drawer.Popup` + `Drawer.Content` + `Drawer.Title`
 * + `Drawer.Description` + `Drawer.Close`. Each sub-component mirrors `@base-ui/react/drawer`
 * 1:1 and adds Orus token styling + `data-slot`.
 *
 * - Primitives only — no `Header` / `Body` / `Footer` sugar, same doctrine as `Dialog`. For the
 *   assembled side-panel shape use `patterns/side-panel`.
 * - `Drawer.Portal` defaults its container to the nearest pharaoh-next scope
 *   (`PortalContainerContext`), falling back to `document.body`.
 * - `swipeDirection` on `Root` drives both the edge the drawer sits on and the dismissal
 *   gesture. Base UI defaults to `'down'` (bottom sheet); pass `'right'` for a side panel.
 * - The `size` variant on `Popup` sets a width and therefore only affects the horizontal
 *   directions. Vertical drawers size to their content, capped at `calc(100dvh - 6rem)`.
 * - Snap points: pass `snapPoints` to `Root`, and set `[--drawer-content-height:100dvh]` on
 *   `Popup` plus `[--drawer-overlay-min-opacity:0.5]` on `Backdrop` via `className`. Base UI
 *   exposes no "has snap points" attribute, so this one knob stays at the call site rather
 *   than forcing a React context on every consumer.
 *
 * Supported Base UI `data-*` states on `Popup`: `data-open`, `data-closed`,
 * `data-starting-style`, `data-ending-style`, `data-expanded`, `data-nested-drawer-open`,
 * `data-nested-drawer-swiping`, `data-swipe-dismiss`, `data-swipe-direction`, `data-swiping`.
 * `Backdrop` exposes `--drawer-swipe-progress`.
 *
 * Consumer-facing CSS variables read by the ported recipe (opt-in overrides, set via
 * `className`/`style`; Base UI does NOT emit these):
 * - `--drawer-inset` — gap between the panel and the viewport edge. @default `0px` (flush).
 * - `--drawer-bleed-background` — colour of the swipe-overshoot area past the panel edge.
 *   Defaults to the panel surface token. This is a knob for consumers, not dead code.
 * - `--drawer-overlay-min-opacity` — floor for the backdrop opacity while swiping. Relevant
 *   when using snap points.
 *
 * @summary Composable drawer primitive (Base UI namespace).
 */
const Drawer = {
  Root: DrawerRoot,
  Trigger: DrawerTrigger,
  Portal: DrawerPortal,
  Backdrop: DrawerBackdrop,
  Viewport: DrawerViewport,
  Popup: DrawerPopup,
  Content: DrawerContent,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Close: DrawerClose,
}

namespace Drawer {
  export namespace Root {
    export type Props = DrawerRoot.Props
  }
  export namespace Trigger {
    export type Props = DrawerTrigger.Props
  }
  export namespace Portal {
    export type Props = DrawerPortal.Props
  }
  export namespace Backdrop {
    export type Props = DrawerBackdrop.Props
  }
  export namespace Viewport {
    export type Props = DrawerViewport.Props
  }
  export namespace Popup {
    export type Props = DrawerPopup.Props
  }
  export namespace Content {
    export type Props = DrawerContent.Props
  }
  export namespace Title {
    export type Props = DrawerTitle.Props
  }
  export namespace Description {
    export type Props = DrawerDescription.Props
  }
  export namespace Close {
    export type Props = DrawerClose.Props
  }
}

export { Drawer, popupVariants }
